import Permissions from "../../../utils/permissions";
import AccessDenied from "../../shared/common/accessDenied";
import React, { useEffect, useState } from "react";
import AllowedTo from "../../shared/common/rbac";
import Breadcrumb from "../../shared/common/breadcrumb";
import { IBreadcrumb } from "../../../model/interface/breadcrumb";
import constant from "../../../utils/constant";
import { useForm } from "react-hook-form";
import Loader from "../../shared/loader/loader";
import {
  addLessonPlanAsset,
  getLessonPlanAssetById,
  getLessonPlanAssetUrl,
  updateLessonPlanAsset,
} from "../../../api/lessonPlans.service";
import { toast } from "react-toastify";
import { Link, useParams, useNavigate } from "react-router";
import Constant from "../../../utils/constant";
import { FilePreview } from "../../shared/common/FilePreview";

export const LessonPlanAssetEdit = () => {
  const params = useParams<{
    lessonPlanId;
    lessonPlanAssetId;
  }>();
  const navigate = useNavigate();
  const lessonPlanId = params.lessonPlanId;
  const [lessonPlanAssetId, setLessonPlanAssetId] = useState<number>(
    isNaN(Number(params.lessonPlanAssetId))
      ? 0
      : Number(params.lessonPlanAssetId)
  );
  const [breadCrumbs, setBreadCrumbs] = useState<Array<IBreadcrumb>>([]);
  const [fileUrl, setFileUrl] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    resetField,
  } = useForm();

  const breadcrumbItems = () => {
    const items: Array<IBreadcrumb> = [
      {
        name: "Lesson Plans",
        url: constant.LESSONPLANS,
      },
      {
        name: `Lesson Plan (${lessonPlanId.toString()})`,
        url: constant.LESSONPLANS_EDIT.replace(
          ":lessonPlanId",
          lessonPlanId.toString()
        ),
      },
      {
        name:
          lessonPlanAssetId > 0
            ? `Asset (${lessonPlanAssetId})`
            : "Asset (New)",
        url: "",
      },
    ];

    return items;
  };

  const errorsHandler = async (errors) => {
    console.log("Errors", errors);
  };

  const submitHandler = async (data) => {
    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", data.title);
    formData.append("ordinal", data.ordinal);
    formData.append("standardCodes", data["standardCodes"]);
    formData.append("description", data.description);
    formData.append("isActive", data.isActive.toString());
    formData.append(
      "assetFile",
      data.assetFile?.length > 0 ? data.assetFile[0] : ""
    );

    if (lessonPlanAssetId > 0) {
      formData.append("lessonPlanAssetId", lessonPlanAssetId.toString());
      updateLessonPlanAsset(lessonPlanId, lessonPlanAssetId, formData)
        .then((response) => {
          loadAssetFile();
          resetField("assetFile");
          setIsLoading(false);
          toast.success("Lesson plan asset updated successfully");
        })
        .catch((error) => {
          setIsLoading(false);
          toast.error("Failed to update lesson plan asset");
        });
    } else {
      addLessonPlanAsset(lessonPlanId, formData)
        .then((response) => {
          resetField("assetFile");
          setIsLoading(false);
          setLessonPlanAssetId(response.data);
          navigate(
            Constant.LESSONPLANS_ASSETS_EDIT.replace(
              ":lessonPlanId",
              lessonPlanId.toString()
            ).replace(":lessonPlanAssetId", response.data.toString())
          );
          toast.success("Lesson plan asset added successfully");
        })
        .catch((error) => {
          setIsLoading(false);
          toast.error("Failed to add lesson plan asset");
        });
    }

    setIsLoading(false);
  };

  const loadAssetFile = () => {
    getLessonPlanAssetUrl(lessonPlanAssetId, lessonPlanId)
      .then((response) => {
        setFileUrl(response.data);
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Failed to retrieve the url for the asset file");
      });
  };

  useEffect(() => {
    if (lessonPlanAssetId) {
      loadAssetFile();
    }
  }, [lessonPlanAssetId]);

  useEffect(() => {
    setBreadCrumbs(breadcrumbItems());
  }, [lessonPlanAssetId]);

  useEffect(() => {
    if (lessonPlanAssetId === 0) return;

    setIsLoading(true);
    // get lesson plan asset by id
    getLessonPlanAssetById(lessonPlanAssetId, lessonPlanId)
      .then((response) => {
        if (response.data) {
          reset(response.data);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error("Failed to load lesson plan asset");
      });

    loadAssetFile();
  }, []);

  return (
    <AllowedTo
      perform={[Permissions.supersheet_create, Permissions.lesson_plan_modify]}
      no={() => <AccessDenied />}
    >
      {isLoading && <Loader />}
      <div className="overlay-image-main pointer-events-none fixed left-0 top-0 w-screen h-screen z-20"></div>
      <div className="flex-1 overflow-auto">
        <div className="min-h-half rounded-bl-3xl bg-site-teal m-7 mb-0 pt-0 ">
          <div className="h-full relative py-1">
            <div className="flex items-center justify-between pb-2">
              <h3 className="text-lg font-light text-gray-900">
                Lesson Plan Asset
              </h3>
            </div>
            <Breadcrumb items={breadCrumbs} />
            <div className="flex space-x-8 h-full">
              <div className="w-1/4 border rounded-lg shadow px-6">
                <form onSubmit={handleSubmit(submitHandler, errorsHandler)}>
                  <div className=" py-6  mt-4 px-6">
                    <div className="grid grid-cols-1 mt-2">
                      <label className={`text-sm font-medium text-gray-700`}>
                        Title
                      </label>

                      <div>
                        <input
                          id="title"
                          autoComplete="off"
                          type="text"
                          className={`block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm w-full`}
                          {...register("title", {
                            required: true,
                          })}
                        />
                        {errors.title && errors.title.type == "required" && (
                          <span className="text-red-700 inline-block pt-1 text-sm">
                            Title is required
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 mt-2">
                      <label className={`text-sm font-medium text-gray-700`}>
                        Position (Numbers only)
                      </label>

                      <div>
                        <input
                          id="ordinal"
                          autoComplete="off"
                          type="text"
                          className={`block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm w-full`}
                          {...register("ordinal", {
                            required: true,
                            pattern: {
                              value: /^[0-9]*$/i,
                              message: "invalid email address",
                            },
                          })}
                        />
                        {errors.ordinal &&
                          errors.ordinal.type == "required" && (
                            <span className="text-red-700 inline-block pt-1 text-sm">
                              Position is required
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 mt-2">
                      <label className={`text-sm font-medium text-gray-700`}>
                        Standard Codes
                      </label>

                      <div>
                        <input
                          id="standardCodes"
                          autoComplete="off"
                          type="text"
                          className={`block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm w-full`}
                          {...register("standardCodes", {
                            required: false,
                          })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 mt-2">
                      <label className={`text-sm font-medium text-gray-700`}>
                        Description
                      </label>

                      <div>
                        <textarea
                          id="description"
                          autoComplete="off"
                          className={`h-32 block border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm w-full`}
                          {...register("description", {
                            required: true,
                          })}
                        />
                        {errors.description &&
                          errors.description.type == "required" && (
                            <span className="text-red-700 inline-block pt-1 text-sm">
                              Description is required
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 mt-2">
                      <div>
                        <input
                          id="isActive"
                          type="checkbox"
                          className=" focus:ring-primary-color h-4 w-4 text-primary-color border-gray-300 rounded"
                          {...register("isActive", {
                            required: false,
                          })}
                        />
                        <label className="ml-1.5 py-2 text-sm text-gray-700">
                          Is Active
                        </label>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 mt-5">
                      <div className="text-sm flex items-center">
                        <label className="font-medium text-gray-700">
                          New Assset PDF
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="file"
                          id="assetFile"
                          accept={".pdf"}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-primary-color focus:border-primary-color dark:focus:border-gray-500 dark:focus:ring-gray-500 dark:bg-custom-black dark:text-white sm:text-sm"
                          {...register("assetFile", {
                            required: lessonPlanAssetId === 0,
                          })}
                        />
                      </div>
                      <div>
                        {errors.assetFile &&
                          errors.assetFile.type == "required" && (
                            <span className="text-red-700 inline-block pt-1 text-sm">
                              An asset file is required
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="flex justify-between pb-2 border-t mt-5 items-center">
                      <div>
                        <button
                          type="submit"
                          className=" cursor-pointer inline-flex items-center px-3 py-2 mt-4 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white dark:text-white bg-primary-color hover:bg-secondary-color focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color"
                        >
                          {lessonPlanAssetId > 0 ? "Update" : "Save"}
                        </button>
                        <Link
                          to={constant.LESSONPLANS_EDIT.replace(
                            ":lessonPlanId",
                            lessonPlanId.toString()
                          )}
                          className="ml-2"
                        >
                          <button className="inline-flex items-center px-3 py-2 my-3 hover:text-gray-500 border border-gray-300 text-sm leading-4 font-medium rounded-md shadow-sm text-gray-700 bg-white  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-color">
                            Cancel
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className="w-3/4">
                <FilePreview
                  title={"Lesson Plan File"}
                  url={fileUrl}
                ></FilePreview>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllowedTo>
  );
};
